import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Clock,
  EyeOff,
  RefreshCw,
  Truck,
  User,
} from 'lucide-react'

import { AppShell } from '@/components/layout/app-shell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Spinner } from '@/components/ui/spinner'
import { useApi } from '@/hooks/use-api'
import { useSyncStatus } from '@/hooks/use-sync-status'
import { listPendingMissions } from '@/lib/offline/db'
import { triggerSync } from '@/lib/offline/sync-controller'
import { resetMissionForRetry } from '@/lib/offline/sync-queue'
import { type PendingMission } from '@/lib/offline/types'

/**
 * Driver dashboard.
 *
 * Backed by `GET /api/planning/my-dashboard`. The endpoint already
 * returns:
 *   - one row per planning the driver hasn't dismissed yet,
 *   - sorted by date ASC (next first),
 *   - with `hasMission` + `canDismiss` flags computed by the
 *     `MissionChecker` so we don't have to do the matching here.
 *
 * Per-row actions:
 *   - "Remplir le bon" → /missions/new?planningId=...
 *   - "Masquer" (when canDismiss) → POST /api/planning/:id/dismiss,
 *     then optimistically remove from the list.
 *
 * Empty state below the list nudges to "Nouvelle mission libre"
 * (no upstream planning).
 */

interface DashboardEntry {
  id: string
  driverId: string
  driverName: string | null
  vehicleId: string | null
  vehicleRegistration: string | null
  clientId: string | null
  clientName: string | null
  templateId: string | null
  site: string
  date: string
  startTime: string | null
  endTime: string | null
  details: string | null
  isDaily: boolean
  hasMission: boolean
  canDismiss: boolean
  createdAt: string
  updatedAt: string
}

const QUERY_KEY = ['planning', 'my-dashboard'] as const
const PENDING_QUERY_KEY = ['offline', 'pending-missions'] as const

export function DashboardPage() {
  const api = useApi()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { pendingCount, errorCount, syncing } = useSyncStatus()

  const dashboardQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<DashboardEntry[]>('/api/planning/my-dashboard'),
  })

  // Pending missions live in IndexedDB. We poll the sync-store
  // counts for invalidation: every time the queue mutates, the
  // counters change and TanStack re-fetches the IDB list.
  const pendingQuery = useQuery({
    queryKey: [...PENDING_QUERY_KEY, pendingCount, errorCount, syncing] as const,
    queryFn: () => listPendingMissions(),
    // Don't hit the network for this — it reads from IDB.
    staleTime: 0,
    gcTime: 0,
  })

  const dismissMutation = useMutation({
    mutationFn: (planningId: string) =>
      api.post<void>(`/api/planning/${planningId}/dismiss`),
    onSuccess: (_data, planningId) => {
      // Optimistic-feeling update: drop the row from the cache so
      // the list shrinks immediately, then revalidate in the
      // background.
      queryClient.setQueryData<DashboardEntry[] | undefined>(QUERY_KEY, (prev) =>
        prev === undefined ? prev : prev.filter((row) => row.id !== planningId),
      )
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })

  const groups = groupByDateBucket(dashboardQuery.data ?? [])

  const pendingMissions = pendingQuery.data ?? []

  return (
    <AppShell>
      {/* Pending missions section — only visible when something
          waits in the offline queue. */}
      {pendingMissions.length > 0 ? (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide mb-2 px-1 flex items-center gap-2">
            <Clock size={16} aria-hidden="true" />
            Missions en attente d'envoi
          </h2>
          <div className="flex flex-col gap-2">
            {pendingMissions.map((mission) => (
              <PendingMissionCard
                key={mission.localId}
                mission={mission}
                onRetry={async () => {
                  await resetMissionForRetry(mission.localId)
                  await triggerSync(api)
                  await queryClient.invalidateQueries({ queryKey: PENDING_QUERY_KEY })
                }}
              />
            ))}
          </div>
        </section>
      ) : null}

      <div className="flex items-baseline justify-between mb-4">
        <h1 className="text-2xl font-bold text-text">Mes plannings</h1>
        <p className="text-sm text-text-muted">{formatToday()}</p>
      </div>

      {dashboardQuery.isLoading ? (
        <div className="py-12 flex justify-center">
          <Spinner size={28} label="Chargement des plannings" />
        </div>
      ) : dashboardQuery.isError ? (
        <Card className="p-4 text-danger">
          Impossible de charger les plannings. Tirez vers le bas pour réessayer.
        </Card>
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<CalendarClock size={28} />}
          title="Aucun planning à venir"
          body="Quand votre administrateur vous attribuera un planning, il apparaîtra ici."
          action={
            <Button
              variant="secondary"
              onClick={() => navigate({ to: '/missions/new' })}
            >
              Créer une mission libre
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-6">
          {groups.map((group) => (
            <section key={group.label}>
              <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide mb-2 px-1">
                {group.label}
              </h2>
              <div className="flex flex-col gap-3">
                {group.entries.map((entry) => (
                  <PlanningCard
                    key={entry.id}
                    entry={entry}
                    onFill={() =>
                      navigate({
                        to: '/missions/new',
                        search: { planningId: entry.id },
                      })
                    }
                    onDismiss={() => dismissMutation.mutate(entry.id)}
                    dismissing={
                      dismissMutation.isPending &&
                      dismissMutation.variables === entry.id
                    }
                  />
                ))}
              </div>
            </section>
          ))}

          {/* Always-visible action to start an unscheduled mission. */}
          <div className="pt-2">
            <Button
              variant="secondary"
              fullWidth
              size="lg"
              onClick={() => navigate({ to: '/missions/new' })}
            >
              <ClipboardList size={20} />
              Créer une mission sans planning
            </Button>
          </div>
        </div>
      )}
    </AppShell>
  )
}

/* -------------------------------------------------------------------------- */
/*  Planning card                                                              */
/* -------------------------------------------------------------------------- */

interface PlanningCardProps {
  entry: DashboardEntry
  onFill: () => void
  onDismiss: () => void
  dismissing: boolean
}

function PlanningCard({ entry, onFill, onDismiss, dismissing }: PlanningCardProps) {
  return (
    <Card className="p-4 flex flex-col gap-3">
      {/* Headline: site + day flag */}
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg font-semibold text-text">{entry.site}</h3>
        {entry.isDaily ? <Badge tone="template">Journalier</Badge> : null}
      </div>

      {/* Meta lines: client, vehicle, hours */}
      <dl className="flex flex-col gap-1 text-sm text-text-muted">
        {entry.clientName !== null ? (
          <div className="flex items-center gap-2">
            <User size={16} aria-hidden="true" />
            <dt className="sr-only">Client</dt>
            <dd>{entry.clientName}</dd>
          </div>
        ) : null}
        {entry.vehicleRegistration !== null ? (
          <div className="flex items-center gap-2">
            <Truck size={16} aria-hidden="true" />
            <dt className="sr-only">Véhicule</dt>
            <dd>{entry.vehicleRegistration}</dd>
          </div>
        ) : null}
        {entry.startTime !== null && entry.endTime !== null ? (
          <div className="flex items-center gap-2">
            <CalendarClock size={16} aria-hidden="true" />
            <dt className="sr-only">Horaires</dt>
            <dd>
              {entry.startTime} → {entry.endTime}
            </dd>
          </div>
        ) : null}
      </dl>

      {/* Status badges */}
      {entry.hasMission ? (
        <div>
          <Badge tone="success">
            <CheckCircle2 size={12} aria-hidden="true" />
            Bon rempli
          </Badge>
        </div>
      ) : null}

      {/* Actions */}
      <div className="flex flex-col gap-2 pt-1">
        <Button variant="primary" size="lg" fullWidth onClick={onFill}>
          {entry.hasMission ? 'Voir / modifier le bon' : 'Remplir le bon'}
        </Button>
        {entry.canDismiss ? (
          <Button
            variant="ghost"
            size="md"
            fullWidth
            onClick={onDismiss}
            loading={dismissing}
            disabled={dismissing}
          >
            <EyeOff size={18} />
            Masquer ce planning
          </Button>
        ) : null}
      </div>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */
/*  Pending mission card (offline queue)                                       */
/* -------------------------------------------------------------------------- */

interface PendingMissionCardProps {
  mission: PendingMission
  onRetry: () => Promise<void>
}

function PendingMissionCard({ mission, onRetry }: PendingMissionCardProps) {
  const isError = mission.status === 'error'
  const isSyncing = mission.status === 'syncing'

  return (
    <Card
      className={[
        'p-3 flex flex-col gap-2',
        isError ? 'border-danger' : 'border-warning',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-text truncate">{mission.payload.site}</p>
          <p className="text-xs text-text-muted">
            Saisi à {formatLocalTime(mission.createdAt)} ·{' '}
            {mission.payload.date}
          </p>
        </div>
        <Badge tone={isError ? 'danger' : isSyncing ? 'info' : 'warning'}>
          {isError ? (
            <>
              <AlertCircle size={12} aria-hidden="true" />
              Erreur
            </>
          ) : isSyncing ? (
            <>
              <RefreshCw size={12} className="animate-spin" aria-hidden="true" />
              Envoi…
            </>
          ) : (
            <>
              <Clock size={12} aria-hidden="true" />
              En attente
            </>
          )}
        </Badge>
      </div>
      {isError && mission.lastError !== null ? (
        <p className="text-xs text-danger">{mission.lastError}</p>
      ) : null}
      {isError ? (
        <Button variant="secondary" size="md" onClick={() => void onRetry()}>
          <RefreshCw size={16} />
          Réessayer
        </Button>
      ) : null}
    </Card>
  )
}

function formatLocalTime(epoch: number): string {
  return new Date(epoch).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

interface DateGroup {
  label: string
  entries: DashboardEntry[]
}

/**
 * Bucket entries into "Aujourd'hui / Demain / Cette semaine /
 * Plus tard" so the dashboard reads top-to-bottom in priority
 * order. Same heuristic the V1 used.
 */
function groupByDateBucket(entries: DashboardEntry[]): DateGroup[] {
  const today = startOfDay(new Date())
  const tomorrow = addDays(today, 1)
  const endOfWeek = addDays(today, 7)

  const buckets: Record<'today' | 'tomorrow' | 'week' | 'later', DashboardEntry[]> = {
    today: [],
    tomorrow: [],
    week: [],
    later: [],
  }

  for (const entry of entries) {
    const d = parseISODate(entry.date)
    if (sameDay(d, today)) buckets.today.push(entry)
    else if (sameDay(d, tomorrow)) buckets.tomorrow.push(entry)
    else if (d >= today && d < endOfWeek) buckets.week.push(entry)
    else buckets.later.push(entry)
  }

  const groups: DateGroup[] = []
  if (buckets.today.length > 0) groups.push({ label: "Aujourd'hui", entries: buckets.today })
  if (buckets.tomorrow.length > 0) groups.push({ label: 'Demain', entries: buckets.tomorrow })
  if (buckets.week.length > 0) groups.push({ label: 'Cette semaine', entries: buckets.week })
  if (buckets.later.length > 0) groups.push({ label: 'Plus tard', entries: buckets.later })
  return groups
}

function startOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function parseISODate(iso: string): Date {
  // The API hands us "YYYY-MM-DD" strings. Build a *local* Date so
  // "today" comparisons don't drift across timezones.
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1)
}

function formatToday(): string {
  return new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}
